/**
 * Permission model (ADR-0005): flat permission strings grouped by feature area;
 * roles are named permission sets per organization. The client only *reflects*
 * permissions for UX (hide/disable + the "Permission denied" screen); the API is
 * the enforcement point.
 */
export const PERMISSIONS = {
  // Sales
  "sales.invoices.view": "View invoices",
  "sales.invoices.create": "Create invoices",
  "sales.invoices.approve": "Approve invoices",
  "sales.quotations.view": "View quotations",
  "sales.quotations.create": "Create quotations",
  "sales.creditnotes.view": "View credit notes",
  "sales.creditnotes.create": "Create credit notes",
  "sales.receipts.view": "View receipts",
  "sales.receipts.create": "Record receipts",
  // Purchases
  "purchases.orders.view": "View purchase orders",
  "purchases.orders.create": "Create purchase orders",
  "purchases.orders.send": "Send purchase orders",
  "purchases.bills.view": "View bills",
  "purchases.bills.create": "Create bills",
  "purchases.bills.approve": "Approve bills",
  "purchases.payments.view": "View supplier payments",
  "purchases.payments.create": "Record supplier payments",
  "purchases.payments.post": "Post supplier payments",
  // Separate from bills for the same reason credit notes are separate from invoices: a debit note
  // reduces a purchase already reported.
  "purchases.debitnotes.view": "View debit notes",
  "purchases.debitnotes.create": "Create debit notes",
  "purchases.debitnotes.approve": "Approve debit notes",
  // Banking & accounting
  "banking.transfers.view": "View fund transfers",
  "banking.transfers.create": "Record fund transfers",
  "banking.transfers.approve": "Post fund transfers",
  "banking.reconciliation.view": "View bank reconciliation",
  // A different job from recording a transfer: that moves money, this accounts for money already
  // moved. It posts nothing, which is why it is not gated on a posting permission.
  "banking.reconciliation.manage": "Reconcile bank accounts",
  "accounting.journals.view": "View journals",
  "accounting.journals.create": "Enter journals",
  "accounting.journals.post": "Post journals",
  "accounting.approvals.act": "Act on approvals",
  "accounting.periods.manage": "Close periods and fiscal years",
  "accounting.periods.view": "View fiscal years",
  // Separate from .manage on purpose: closing is routine month-end work; reopening undoes a figure
  // somebody has already reported. Administrator-only by default (ADR-0007 §4).
  "accounting.periods.reopen": "Reopen a closed period",
  "accounting.yearclose.approve": "Approve a year's close",
  "accounting.employees.view": "See employee ledgers",
  "accounting.employees.record": "Record payments to and receipts from employees",
  "accounting.coa.manage": "Maintain chart of accounts",
  "accounting.coa.view": "View chart of accounts",
  // Reports
  "reports.view": "View reports",
  "reports.layout.edit": "Edit report layouts",
  // Settings
  "settings.company.manage": "Manage company setup",
  "settings.numbering.manage": "Manage numbering",
  "settings.numbering.view": "View numbering",
  "settings.tax.manage": "Manage tax codes",
  "settings.tax.view": "View tax codes",
  "settings.users.manage": "Manage users and roles",
  "settings.audit.view": "View audit trail",
  "settings.parties.view": "View customers and suppliers",
  "settings.parties.manage": "Manage customers and suppliers",
  "settings.approvals.manage": "Change segregation of duties",
  // Subscribing and exporting the books when they're locked: the Administrator's (suite ADR-0096).
  "settings.subscription.manage": "Manage the subscription and export the books",
  // Help and support (ADR-0010), served by the platform support module. Every role reads the
  // guides; Administrator and Accountant raise tickets.
  "support.help.view": "Read help guides",
  "support.tickets.manage": "Raise and follow support tickets",
} as const;

export type Permission = keyof typeof PERMISSIONS;

export type RoleName = "Administrator" | "Accountant" | "Verifier" | "Approver" | "View-only";

export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

const VIEW_ONLY: Permission[] = ALL_PERMISSIONS.filter((p) => p.endsWith(".view"));

/** Default role → permission sets. Tenants can clone and edit these. */
export const ROLE_PERMISSIONS: Record<RoleName, Permission[]> = {
  Administrator: ALL_PERMISSIONS,
  Accountant: ALL_PERMISSIONS.filter(
    (p) =>
      !p.startsWith("settings.users") &&
      p !== "settings.approvals.manage" &&
      p !== "settings.subscription.manage" &&
      !p.endsWith(".approve") &&
      p !== "accounting.approvals.act" &&
      p !== "accounting.periods.reopen" &&
      p !== "accounting.yearclose.approve",
  ),
  Verifier: [...VIEW_ONLY, "accounting.approvals.act"],
  Approver: [
    ...VIEW_ONLY,
    "sales.invoices.approve",
    "purchases.bills.approve",
    "purchases.debitnotes.approve",
    "banking.transfers.approve",
    "accounting.journals.post",
    "purchases.payments.post",
    "accounting.yearclose.approve",
    "purchases.orders.send",
    "accounting.approvals.act",
  ],
  "View-only": VIEW_ONLY,
};

export function hasPermission(
  granted: ReadonlySet<Permission> | Permission[],
  p: Permission,
): boolean {
  return Array.isArray(granted) ? granted.includes(p) : granted.has(p);
}

/** Human sentence for the Permission denied screen: "Creating invoices needs the Accountant role". */
export function deniedMessage(p: Permission): string {
  const label = PERMISSIONS[p].replace(/^(\w+)/, (w) => w.replace(/e?$/, "") + "ing");
  const role: RoleName = p.startsWith("settings.")
    ? "Administrator"
    : p.endsWith(".approve") || p === "accounting.approvals.act"
      ? "Approver"
      : "Accountant";
  return `${label.charAt(0).toUpperCase() + label.slice(1)} needs the ${role} role`;
}

/**
 * The role names, in catalogue order.
 *
 * Derived from `ROLE_PERMISSIONS` rather than listed again: a screen offering a role the catalogue
 * has never heard of is a screen that can send an invitation nobody can accept.
 */
export const ROLE_NAMES = Object.keys(ROLE_PERMISSIONS) as RoleName[];
